import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../session/session.service';
import { LogEvent } from './log-events';

/**
 * GELF/syslog severity levels used by the AMIE logging pipeline.
 *
 * @ticket EB-3798
 * The integer values are the syslog severities Graylog expects (verified
 * against the EVA + SMT precedents): higher int = more verbose. The level
 * gate in {@link LoggingService} drops any event whose level exceeds the
 * current threshold, so DEBUG (7) only ships when the threshold is raised.
 */
export enum LogLevel {
  error = 3,
  warn = 4,
  info = 6,
  debug = 7,
}

/**
 * Canonical GELF 1.1 payload AMIE POSTs to the Graylog HTTP ingest.
 *
 * @ticket EB-3798
 * Only the fields AMIE actually emits (D1) — deliberately NOT the 25-field
 * EVA `LogData`; EVA's request/response-body capture is a PII risk and is not
 * copied. Custom fields are `_`-prefixed per the GELF spec; `eventType`
 * separates health (`operational`) from behaviour (`usage`) on one stream.
 * `timestamp` is UNIX SECONDS (not millis). Unknown identity fields
 * (`_userId` when the user is unloaded, `_brandId` when no customer is open)
 * are OMITTED rather than sent as 0/null.
 */
export interface GelfEntry {
  version: '1.1';
  host: string;
  short_message: string;
  full_message?: string;
  level: number;
  timestamp: number;
  facility: 'GELF';
  project: 'amie';
  eventType: 'operational' | 'usage';
  _appVersion: string;
  _userId?: number;
  _brandId?: number;
  correlationId?: string;
  [customField: string]: unknown;
}

/**
 * Extra GELF fields a caller may attach to a log event (business IDs, HTTP
 * status, durations, and — for request-bound events only — a `correlationId`).
 * A `full_message` here becomes the GELF detail/stack line.
 *
 * CONTRACT: values should be PRIMITIVES (string/number/boolean). Never pass PII
 * (names/tokens/bodies/query strings). A non-primitive is JSON-serialised into a
 * single `keyValuePairs` string as a safety net — do NOT rely on that to log a
 * DTO, as it would serialise every field (PII included) into Graylog.
 */
export type LogFields = Record<string, unknown>;

/**
 * Request body for the `customersupport-api` log bridge
 * (`POST /customersupport/v1/logmessage`). A subset of the bridge's `LogData`
 * contract — only the columns AMIE populates; every other GELF custom field
 * (eventType, correlationId, business IDs) travels in {@link keyValuePairs},
 * which the bridge forwards to Graylog as `_`-prefixed custom fields.
 *
 * @ticket EB-3798
 * `project` identifies the emitting app (`'amie'`) — the bridge records it as
 * the log source. `_environment` carries the resolved env for filtering.
 */
export interface LogMessage {
  host: string;
  short_message: string;
  full_message?: string;
  level: number;
  project: string;
  _appVersion: string;
  _environment: string;
  _userId?: number;
  _brandId?: number;
  /**
   * Typed first-class bridge columns (see `customersupport-api` `MessageMapper`):
   * `_httpStatus` is an int the bridge writes to Graylog with its numeric type,
   * so `httpStatus:>=500` range queries work — unlike a `keyValuePairs` string.
   * Populated from the matching bare emit-site keys via {@link buildBridgePayload}.
   */
  _httpStatus?: number;
  _method?: string;
  _requestedUrl?: string;
  keyValuePairs: { key: string; value: string }[];
}

/**
 * Structured frontend logging for AMIE — builds a canonical GELF-shaped entry
 * and, in the LIVE environment, POSTs it to the `customersupport-api` log
 * bridge (which forwards to Graylog); in non-live (test / local) it mirrors to
 * the console only, UNLESS a per-session opt-in ({@link setRemoteLogging})
 * forces the real POST for on-test verification.
 *
 * @ticket EB-3798
 * The send decision is driven by the resolved ENVIRONMENT (`portalEnvKey`),
 * NOT the build type (`environment.production`): a dev-type build can be
 * deployed to live, and it must still ship logs there. `portalEnvKey` is
 * injected per deploy and is the same signal the transcript fallback uses to
 * detect live.
 *
 * Design highlights:
 * - Transport = the `customersupport-api` bridge (D2/D15, EVA precedent):
 *   `POST {customerSupportUrl}/logmessage`, the BE forwards to Graylog GELF.
 *   AMIE is served from a PUBLIC CloudFront origin, so a direct browser→syslog
 *   POST would (a) be blocked by the CSP `connect-src` (the internal GELF port
 *   :12205 is not — and must not be — whitelisted) and (b) leak the internal
 *   syslog host:port into a world-readable CSP. The bridge lives on the normal
 *   API host (`*.ebuero.de` / `*.office-platform.net`, already CSP-allowed) so
 *   the internal host stays server-side. The GELF entry is mapped to the
 *   bridge's `LogMessage` shape in {@link buildBridgePayload}.
 * - The POST still goes out through a bare `HttpClient(HttpBackend)` so it
 *   BYPASSES the interceptor chain (D13): it can never re-enter the HTTP-error
 *   interceptor → no logging recursion. The bridge is OUR OWN authenticated
 *   API (unlike the old foreign syslog host), so the Bearer token is attached
 *   MANUALLY here when present — safe, and required if the endpoint is guarded.
 *   Fire-and-forget: errors are swallowed, never retried, never surfaced.
 * - Identity fields (`_userId`/`_brandId`) are resolved LAZILY at log time via
 *   the {@link Injector} — constructor-injecting AuthService/SessionService
 *   would create a circular dependency once those services log through here.
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {
  /** sessionStorage key for the D12 verbose (DEBUG) opt-in — session-scoped so it auto-clears on tab close. */
  private static readonly VERBOSE_KEY = 'cmt-verbose-logging';

  /**
   * sessionStorage key for the on-demand remote-logging opt-in. When set, the
   * real GELF POST fires even in a non-live environment — used to verify the
   * transport on test (e.g. the R1 CORS probe) without a live deploy.
   * Session-scoped so it auto-clears on tab close (never localStorage).
   */
  private static readonly REMOTE_LOGGING_KEY = 'cmt-remote-logging';

  /**
   * Framework-owned GELF keys that a caller-supplied `LogFields` entry must NOT
   * overwrite (see `buildEntry`). `full_message` and `correlationId` are
   * intentionally absent — those ARE caller-supplied.
   */
  private static readonly RESERVED_ENTRY_KEYS = new Set<string>([
    'version', 'host', 'short_message', 'level', 'timestamp', 'facility',
    'project', 'eventType', '_appVersion', '_userId', '_brandId',
    // `_environment` is set on the bridge payload from the resolved env, not by
    // callers — reserve it here too so a caller field of that name is rejected
    // explicitly in `buildEntry` rather than silently dropped later by the
    // `BRIDGE_COLUMN_KEYS` skip in `buildBridgePayload`.
    '_environment',
  ]);

  /**
   * Client-side send rate limit (per browser/session): at most {@link RATE_MAX}
   * bridge POSTs per {@link RATE_WINDOW_MS}. A backend outage makes every failed
   * request emit an `error.http`; without a cap a single runaway client (an
   * AG-Grid or polling view in a change-detection loop) could flood Graylog.
   * On overflow the events are dropped and one `logging.throttled` summary is
   * emitted when the window rolls. This bounds ONE client's contribution; a
   * fleet-wide surge (many clients, one outage) is inherently a server-side
   * concern (bridge/ingest) and out of scope here — noted in the spec.
   */
  private static readonly RATE_WINDOW_MS = 10_000;
  private static readonly RATE_MAX = 30;
  private rateWindowStart = 0;
  private rateCount = 0;
  private rateDropped = 0;

  /**
   * Per-endpoint dedup for `error.http` within a window, keyed on
   * `method|path|status`: collapses a burst of identical failures (e.g. a grid
   * firing the same failing request many times) to one event per window.
   */
  private static readonly HTTP_DEDUP_WINDOW_MS = 10_000;
  private static readonly HTTP_DEDUP_MAX = 50;
  private readonly httpSeen = new Map<string, number>();

  /**
   * Bare emit-site keys that map to a typed first-class bridge column. Kept out
   * of the `keyValuePairs` bag by {@link buildBridgePayload} so the value lands
   * in Graylog with its real type instead of a string.
   */
  private static readonly PROMOTED_COLUMN_KEYS = new Set<string>([
    'httpStatus', 'method', 'requestedUrl',
  ]);

  /** Max length for `full_message` (stack traces) and custom-field values. */
  private static readonly FULL_MESSAGE_MAX = 4000;
  private static readonly KV_VALUE_MAX = 1000;

  private readonly injector = inject(Injector);

  /**
   * Bare HttpClient built directly on HttpBackend so the log POST skips the
   * interceptor chain (no correlation-id interceptor, and — crucially — no
   * re-entry into the HTTP-error interceptor → no logging recursion) — D13.
   * The Bearer is attached manually in {@link send} (the bridge is our own API).
   */
  private readonly http = new HttpClient(inject(HttpBackend));

  /** Active verbosity threshold (default INFO=6). Events above it are dropped. */
  private readonly currentLogLevel = signal<number>(LogLevel.info);

  /**
   * Read-only D12 verbose state for the UI toggle: true when the threshold is
   * raised to DEBUG. Derived from {@link currentLogLevel} so the switch stays
   * in sync with {@link setVerbose} without exposing the writable signal.
   */
  readonly isVerbose = computed(() => this.currentLogLevel() === LogLevel.debug);

  /**
   * On-demand remote-logging opt-in (non-live only): when true, the real GELF
   * POST fires even on test/local. In live it is irrelevant — live always
   * sends. Seeded from sessionStorage in the constructor.
   */
  private readonly remoteLoggingOptIn = signal<boolean>(false);

  /** Read-only remote-logging state for a future UI toggle (mirrors {@link isVerbose}). */
  readonly isRemoteLogging = computed(() => this.remoteLoggingOptIn());

  constructor() {
    try {
      if (sessionStorage.getItem(LoggingService.VERBOSE_KEY) === 'true') {
        this.currentLogLevel.set(LogLevel.debug);
      }
      if (sessionStorage.getItem(LoggingService.REMOTE_LOGGING_KEY) === 'true') {
        this.remoteLoggingOptIn.set(true);
      }
    } catch {
      // sessionStorage unavailable (e.g. private mode) — keep the defaults.
    }
  }

  /**
   * D12 verbose toggle: raise the threshold to DEBUG (7) when on, back to
   * INFO (6) when off, and persist the choice to sessionStorage so it survives
   * same-tab navigation but auto-clears on tab close (never localStorage).
   */
  setVerbose(on: boolean): void {
    this.currentLogLevel.set(on ? LogLevel.debug : LogLevel.info);
    this.persistFlag(LoggingService.VERBOSE_KEY, on);
  }

  /**
   * Persist a boolean opt-in to sessionStorage (session-scoped, auto-clears on
   * tab close — never localStorage). Shared by {@link setVerbose} and
   * {@link setRemoteLogging}; a failure (private mode) leaves the in-memory
   * state authoritative.
   */
  private persistFlag(key: string, on: boolean): void {
    try {
      if (on) {
        sessionStorage.setItem(key, 'true');
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // sessionStorage unavailable — the in-memory signal still applies.
    }
  }

  /**
   * On-demand remote-logging toggle: force the real GELF POST in a non-live
   * environment (test/local) so the transport can be exercised without a live
   * deploy — e.g. the R1 CORS probe. Persisted to sessionStorage so it survives
   * same-tab navigation but auto-clears on tab close (never localStorage). A
   * no-op effect in live, where logs always ship.
   *
   * Flip it from the devtools console without a UI:
   *   sessionStorage.setItem('cmt-remote-logging','true')  // then reload
   */
  setRemoteLogging(on: boolean): void {
    this.remoteLoggingOptIn.set(on);
    this.persistFlag(LoggingService.REMOTE_LOGGING_KEY, on);
  }

  /** Operational ERROR — unhandled/HTTP-5xx/failed-auth class events. */
  error(shortMessage: LogEvent, fields?: LogFields): void {
    this.log(LogLevel.error, 'operational', shortMessage, fields);
  }

  /** Operational WARN — recoverable/HTTP-4xx/transient class events. */
  warn(shortMessage: LogEvent, fields?: LogFields): void {
    this.log(LogLevel.warn, 'operational', shortMessage, fields);
  }

  /** Operational INFO — routine health/diagnostic events. */
  info(shortMessage: LogEvent, fields?: LogFields): void {
    this.log(LogLevel.info, 'operational', shortMessage, fields);
  }

  /** Operational DEBUG — dev-only detail; only ships when verbose is enabled. */
  debug(shortMessage: LogEvent, fields?: LogFields): void {
    this.log(LogLevel.debug, 'operational', shortMessage, fields);
  }

  /** Product-usage event (behaviour). Always INFO, tagged `eventType:'usage'`. */
  usage(event: LogEvent, fields?: LogFields): void {
    this.log(LogLevel.info, 'usage', event, fields);
  }

  /**
   * Core pipeline: level-gate → build canonical GELF → POST in prod / console
   * mirror in dev. Never throws (logging must not break a feature).
   */
  private log(
    level: LogLevel,
    eventType: 'operational' | 'usage',
    shortMessage: string,
    fields?: LogFields,
  ): void {
    if (level > this.currentLogLevel()) return;

    const entry = this.buildEntry(level, eventType, shortMessage, fields);

    // ENV-driven, NOT build-type: live always ships (a dev-type build deployed
    // to live still must). Non-live mirrors to the console, and additionally
    // ships only when the on-demand opt-in is set (R1 CORS probe / on-test
    // debugging) — keeping console visibility either way.
    const isLive = environment.portalEnvKey === 'live';
    const shouldSend = isLive || this.remoteLoggingOptIn();

    // Flood control applies to the SEND only (protecting the bridge/Graylog),
    // NOT the console mirror — a developer must still see every repeated failure
    // locally. Collapse a burst of identical HTTP failures (same method|path|
    // status) to one POST per window (the biggest per-client flood multiplier),
    // then apply the global rate cap.
    if (
      shouldSend &&
      !(shortMessage === 'error.http' && this.isDuplicateHttp(fields)) &&
      this.allowSend(shortMessage)
    ) {
      this.send(entry);
    }
    if (!isLive) {
      this.mirrorToConsole(level, entry);
    }
  }

  /**
   * Fixed-window rate gate for the send path: allows up to {@link RATE_MAX}
   * POSTs per {@link RATE_WINDOW_MS}. When the window rolls with drops recorded,
   * emits ONE `logging.throttled` summary (which itself bypasses the cap) so the
   * loss is visible in Graylog. Returns false for events dropped by the cap.
   */
  private allowSend(shortMessage: string): boolean {
    const now = Date.now();
    if (now - this.rateWindowStart >= LoggingService.RATE_WINDOW_MS) {
      const dropped = this.rateDropped;
      this.rateWindowStart = now;
      this.rateCount = 0;
      this.rateDropped = 0;
      if (dropped > 0 && shortMessage !== 'logging.throttled') {
        this.emitThrottleSummary(dropped);
      }
    }
    if (shortMessage === 'logging.throttled') return true;
    if (this.rateCount >= LoggingService.RATE_MAX) {
      this.rateDropped++;
      return false;
    }
    this.rateCount++;
    return true;
  }

  /** Build + send the throttle summary directly (bypasses {@link allowSend}). */
  private emitThrottleSummary(dropped: number): void {
    const entry = this.buildEntry(LogLevel.warn, 'operational', 'logging.throttled', { dropped });
    this.send(entry);
  }

  /**
   * True when an `error.http` with the same `method|path|status` was already
   * emitted inside {@link HTTP_DEDUP_WINDOW_MS}. Evicts stale keys and caps the
   * map size so it can never grow unbounded.
   */
  private isDuplicateHttp(fields?: LogFields): boolean {
    if (!fields) return false;
    const key = `${fields['method']}|${fields['requestedUrl']}|${fields['httpStatus']}`;
    const now = Date.now();
    const last = this.httpSeen.get(key);
    if (last !== undefined && now - last < LoggingService.HTTP_DEDUP_WINDOW_MS) {
      return true;
    }
    this.httpSeen.set(key, now);
    if (this.httpSeen.size > LoggingService.HTTP_DEDUP_MAX) {
      for (const [k, t] of this.httpSeen) {
        if (now - t >= LoggingService.HTTP_DEDUP_WINDOW_MS) this.httpSeen.delete(k);
      }
      const oldest = this.httpSeen.keys().next().value;
      if (this.httpSeen.size > LoggingService.HTTP_DEDUP_MAX && oldest !== undefined) {
        this.httpSeen.delete(oldest);
      }
    }
    return false;
  }

  /** Assemble the canonical GELF body (D1), sanitizing every string value. */
  private buildEntry(
    level: LogLevel,
    eventType: 'operational' | 'usage',
    shortMessage: string,
    fields?: LogFields,
  ): GelfEntry {
    const entry: GelfEntry = {
      version: '1.1',
      host: this.resolveHost(),
      short_message: this.sanitize(shortMessage),
      level,
      timestamp: Date.now() / 1000,
      facility: 'GELF',
      project: 'amie',
      eventType,
      _appVersion: environment.appVersion,
    };

    const userId = this.resolveUserId();
    if (userId !== null) entry._userId = userId;

    const brandId = this.resolveBrandId();
    if (brandId !== null) entry._brandId = brandId;

    if (fields) {
      for (const [key, value] of Object.entries(fields)) {
        // Guard the core GELF keys: a caller field named e.g. `level`, `host`
        // or `_userId` must never overwrite the framework-owned value (it would
        // corrupt the bridge `level` column / Graylog severity). `full_message`
        // and `correlationId` are legitimately caller-supplied, so they stay.
        if (LoggingService.RESERVED_ENTRY_KEYS.has(key)) continue;
        if (key === 'full_message' && typeof value === 'string') {
          // `full_message` is GELF's multi-line detail field (stack traces):
          // preserve newlines (do NOT strip them — that flattens a stack into
          // one unreadable line), just cap the length. It is JSON-escaped in the
          // bridge body, so newlines carry no injection risk here.
          entry.full_message = this.cap(value, LoggingService.FULL_MESSAGE_MAX);
          continue;
        }
        entry[key] = typeof value === 'string' ? this.sanitize(value) : value;
      }
    }

    return entry;
  }

  /**
   * Fire-and-forget POST to the `customersupport-api` log bridge via the
   * interceptor-bypassing HttpClient. The Bearer is attached manually (the
   * bridge is our own API; bypassing the chain is only to prevent error-log
   * recursion, not to hide auth). Errors (network/CSP/bridge outage) are
   * swallowed — never retried (retry would amplify an outage) and never
   * surfaced (would recurse through the error pipeline). See spec
   * `shouldSwallowBridgePostErrorWithoutRecursing`.
   */
  private send(entry: GelfEntry): void {
    const token = this.resolveAccessToken();
    if (!token) {
      // The bridge REQUIRES auth; a token-less POST would 401 and vanish, and
      // the events most likely to be token-less are exactly the auth-failure
      // diagnostics we most need. Skip the doomed POST but keep the loss
      // observable (even in live) instead of silently dropping it.
      console.warn('LoggingService: no access token, log not sent —', entry.short_message);
      return;
    }
    this.http
      .post(`${environment.customerSupportUrl}/logmessage`, this.buildBridgePayload(entry), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        catchError((err: unknown) => {
          // Non-live: surface the failure so a broken pipeline is visible in a
          // support screen-share. Live: swallow (a console error there would be
          // noise and could re-enter the error pipeline).
          if (environment.portalEnvKey !== 'live') {
            console.error('LoggingService: bridge POST failed —', entry.short_message, err);
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * GELF columns that map to a first-class {@link LogMessage} field — every
   * other entry key is forwarded via `keyValuePairs`. `version`/`facility`/
   * `timestamp` are GELF-only framing the bridge re-derives, so they are dropped.
   */
  private static readonly BRIDGE_COLUMN_KEYS = new Set<string>([
    'version', 'host', 'short_message', 'full_message', 'level', 'facility',
    'timestamp', 'project', '_appVersion', '_userId', '_brandId', '_environment',
  ]);

  /**
   * Map the canonical GELF entry onto the bridge's `LogMessage` shape: known
   * columns 1:1, everything else (eventType, correlationId, business IDs) into
   * `keyValuePairs` so nothing is lost — the bridge re-expands them into GELF
   * custom fields for Graylog.
   */
  private buildBridgePayload(entry: GelfEntry): LogMessage {
    const payload: LogMessage = {
      host: entry.host,
      short_message: entry.short_message,
      level: entry.level,
      project: entry.project,
      _appVersion: entry._appVersion,
      _environment: environment.portalEnvKey,
      keyValuePairs: [],
    };
    if (typeof entry.full_message === 'string') payload.full_message = entry.full_message;
    if (typeof entry._userId === 'number') payload._userId = entry._userId;
    if (typeof entry._brandId === 'number') payload._brandId = entry._brandId;

    // Promote the bare emit-site keys that have a typed first-class bridge
    // column, so numbers stay numbers in Graylog (e.g. `httpStatus:>=500`).
    if (typeof entry['httpStatus'] === 'number') payload._httpStatus = entry['httpStatus'];
    if (typeof entry['method'] === 'string') payload._method = this.sanitize(entry['method']);
    if (typeof entry['requestedUrl'] === 'string') {
      payload._requestedUrl = this.sanitize(entry['requestedUrl']);
    }

    for (const [key, value] of Object.entries(entry)) {
      if (LoggingService.BRIDGE_COLUMN_KEYS.has(key)) continue;
      if (LoggingService.PROMOTED_COLUMN_KEYS.has(key)) continue;
      if (value === undefined || value === null) continue;
      // Objects/arrays are JSON-encoded (not `String()` → "[object Object]");
      // both key and value are sanitised, and values are length-capped.
      const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
      payload.keyValuePairs.push({
        key: this.sanitize(key),
        value: this.cap(this.sanitize(raw), LoggingService.KV_VALUE_MAX),
      });
    }

    // The client event time is dropped from the bridge columns (the bridge
    // owns the GELF `timestamp` = its receive time); forward it as a custom
    // field so a burst of client events can still be ordered.
    payload.keyValuePairs.push({ key: 'clientTimestamp', value: String(entry.timestamp) });

    return payload;
  }

  /**
   * The OAuth access token via the library's supported API, or null when
   * unavailable. Resolved lazily through the {@link Injector} (like
   * AuthService/SessionService) so a change to `OAuthStorage` (e.g. switching to
   * localStorage) cannot silently break logging — reading the library's
   * internal storage key directly would.
   */
  private resolveAccessToken(): string | null {
    try {
      return this.injector.get(OAuthService).getAccessToken() || null;
    } catch {
      return null;
    }
  }

  /** Dev/test mirror — maps the level to the matching console method. */
  private mirrorToConsole(level: LogLevel, entry: GelfEntry): void {
    switch (level) {
      case LogLevel.error:
        console.error(entry.short_message, entry);
        break;
      case LogLevel.warn:
        console.warn(entry.short_message, entry);
        break;
      case LogLevel.debug:
        console.debug(entry.short_message, entry);
        break;
      default:
        console.info(entry.short_message, entry);
    }
  }

  /** Strip control chars (`\n \r \t`) to prevent log-injection (catalog §6). */
  private sanitize(value: string): string {
    return value.replace(/[\n\r\t]/g, '');
  }

  /** Truncate to `max` characters (bounds `full_message` + custom-field values). */
  private cap(value: string, max: number): string {
    return value.length > max ? value.slice(0, max) : value;
  }

  private resolveHost(): string {
    try {
      return window.location.hostname;
    } catch {
      return '';
    }
  }

  /**
   * Lazily resolve the logged-in userId; returns null (→ field omitted) when
   * unavailable or when AuthService reports 0 (user not yet loaded).
   */
  private resolveUserId(): number | null {
    try {
      const authService = this.injector.get(AuthService);
      const userId = authService.currentUserId;
      return userId > 0 ? userId : null;
    } catch {
      return null;
    }
  }

  /**
   * Lazily resolve the open customer's brandId; returns null (→ field omitted)
   * when no customer is open or the service is unavailable.
   */
  private resolveBrandId(): number | null {
    try {
      const sessionService = this.injector.get(SessionService);
      return sessionService.brandId() ?? null;
    } catch {
      return null;
    }
  }
}
